import { makeAutoObservable, runInAction } from "mobx";
import {
  superadminPayoutConfirm,
  superadminGetServiceCenterPayouts,
  superadminPayoutRequest,
} from "@pro-sto/api-client";
import type {
  PayoutItemDto as PayoutItem,
  PaginationMetaDto as PaginationMeta,
  SuperadminGetServiceCenterPayoutsData,
} from "@pro-sto/api-client";
import { toastStore } from "../ToastStore";

const DEFAULT_META: PaginationMeta = {
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPrevPage: false,
};

export class PayoutsSubStore {
  // List
  items: PayoutItem[] = [];
  meta: PaginationMeta = { ...DEFAULT_META };
  isLoading = false;
  pendingPayout = 0;
  lastPayoutDate: string | null = null;
  dateFrom = "";
  dateTo = "";
  statusFilter: string[] = [];

  /** Monotonic counter to discard stale responses */
  private reqSeq = 0;

  // Payout modal
  isModalOpen = false;
  sessionId: string | null = null;
  isRequesting = false;
  isConfirming = false;

  constructor(private getUuid: () => string) {
    makeAutoObservable<this, 'reqSeq'>(this, { reqSeq: false });
  }

  setDateRange = (from: string, to: string) => {
    this.dateFrom = from;
    this.dateTo = to;
  };
  setStatusFilter = (val: string[]) => {
    this.statusFilter = val;
  };

  private buildParams(
    page: number,
  ): Omit<SuperadminGetServiceCenterPayoutsData, "uuid"> {
    return {
      page,
      limit: this.meta.limit,
      ...(this.statusFilter.length > 0 && {
        filterStatusEq: this
          .statusFilter[0] as SuperadminGetServiceCenterPayoutsData["filterStatusEq"],
      }),
      ...(this.dateFrom && { filterDateFromEq: this.dateFrom }),
      ...(this.dateTo && { filterDateToEq: this.dateTo }),
    };
  }

  load = async (page = 1) => {
    const uuid = this.getUuid();
    if (!uuid) return;
    const reqId = ++this.reqSeq;
    this.isLoading = true;
    try {
      const res = await superadminGetServiceCenterPayouts({
        uuid,
        ...this.buildParams(page),
      });
      if (reqId !== this.reqSeq) return;
      runInAction(() => {
        this.items = res.data ?? [];
        this.meta = res.meta;
        this.pendingPayout = res.pendingPayout;
        this.lastPayoutDate = (res.lastPayoutDate as string | null) ?? null;
        this.isLoading = false;
      });
    } catch {
      if (reqId !== this.reqSeq) return;
      runInAction(() => {
        this.isLoading = false;
      });
      toastStore.showError("Не удалось загрузить выплаты");
    }
  };

  loadMore = async () => {
    if (!this.meta.hasNextPage || this.isLoading) return;
    const uuid = this.getUuid();
    if (!uuid) return;
    const reqId = ++this.reqSeq;
    this.isLoading = true;
    try {
      const res = await superadminGetServiceCenterPayouts({
        uuid,
        ...this.buildParams(this.meta.page + 1),
      });
      if (reqId !== this.reqSeq) return;
      runInAction(() => {
        const existingUuids = new Set(this.items.map((i) => i.uuid));
        const newItems = (res.data ?? []).filter(
          (i) => !existingUuids.has(i.uuid),
        );
        this.items = [...this.items, ...newItems];
        this.meta = res.meta;
        this.isLoading = false;
      });
    } catch {
      if (reqId !== this.reqSeq) return;
      runInAction(() => {
        this.isLoading = false;
      });
      toastStore.showError("Не удалось загрузить выплаты");
    }
  };

  // ─── Payout modal ───

  openModal = () => {
    if (this.pendingPayout <= 0) return;
    this.isModalOpen = true;
    this.sessionId = null;
  };

  closeModal = () => {
    this.isModalOpen = false;
    this.sessionId = null;
  };

  requestPayout = async () => {
    const uuid = this.getUuid();
    if (!uuid || this.pendingPayout <= 0) return;
    this.isRequesting = true;
    try {
      const res = await superadminPayoutRequest({
        requestBody: {
          targets: [{ serviceCenterId: uuid, amount: this.pendingPayout }],
        },
      });
      runInAction(() => {
        this.sessionId = res.sessionId;
        this.isRequesting = false;
      });
    } catch {
      runInAction(() => {
        this.isRequesting = false;
      });
      toastStore.showError("Не удалось создать запрос на выплату");
    }
  };

  confirmPayout = async (code: string): Promise<boolean> => {
    if (!this.sessionId) return false;
    this.isConfirming = true;
    try {
      await superadminPayoutConfirm({
        requestBody: { sessionId: this.sessionId, code },
      });
      runInAction(() => {
        this.isConfirming = false;
        this.pendingPayout = 0;
      });
      toastStore.showSuccess("Выплата выполнена успешно");
      this.load();
      return true;
    } catch {
      runInAction(() => {
        this.isConfirming = false;
      });
      toastStore.showError("Не удалось подтвердить выплату");
      return false;
    }
  };

  reset = () => {
    this.items = [];
    this.meta = { ...DEFAULT_META };
    this.pendingPayout = 0;
    this.lastPayoutDate = null;
    this.dateFrom = "";
    this.dateTo = "";
    this.statusFilter = [];
  };
}
